// src/components/modals/TermsModal.js
import React, { useState } from 'react';
import { Shield, FileText, CheckSquare, ChevronRight } from 'lucide-react';

// ── Seção interna do modal ─────────────────────────────────────────────────
const Section = ({ title, children }) => (
    <div className="mb-5">
        <h4 className="font-bold text-sm text-[#1B2B6B] mb-2 border-l-2 border-[#C0181C] pl-2">{title}</h4>
        <div className="text-sm text-gray-600 leading-relaxed space-y-1">{children}</div>
    </div>
);

// ── Conteúdo: Termos de Uso ────────────────────────────────────────────────
const TermosDeUso = () => (
    <div>
        <Section title="1. Objeto">
            <p>
                O RPLAY é uma plataforma interna de reconhecimento de desempenho operada pela
                <strong> RP Métodos Business Partners</strong>. O sistema registra e pondera
                indicadores de produtividade, qualidade, comportamento e iniciativa dos colaboradores,
                atribuindo pontos que podem ser trocados por benefícios conforme regulamento vigente.
            </p>
        </Section>

        <Section title="2. Acesso e Autenticação">
            <p>O acesso é restrito a colaboradores com vínculo empregatício ativo com a RP Métodos.</p>
            <p>As credenciais (e-mail e senha) são pessoais e intransferíveis. O compartilhamento
                é vedado e pode sujeitar o colaborador a medidas disciplinares.</p>
            <p>A sessão expira automaticamente após 8 horas de inatividade.</p>
        </Section>

        <Section title="3. Pontuação e Benefícios">
            <p>
                Os pontos atribuídos no RPLAY são parte de um programa de reconhecimento
                da empresa e seguem as regras definidas no Regulamento Interno do Programa de
                Incentivo. A concessão de benefícios está sujeita à disponibilidade de estoque,
                aprovação administrativa e às condições previstas no regulamento.
            </p>
            <p>
                A pontuação não constitui, por si só, direito adquirido a qualquer benefício
                de natureza salarial, exceto quando expressamente previsto em acordo coletivo
                ou regulamento negociado com a representação dos trabalhadores.
            </p>
        </Section>

        <Section title="4. Sugestões e Conteúdo">
            <p>
                Ao enviar sugestões, notícias ou qualquer conteúdo pela plataforma, o colaborador
                declara que o conteúdo é verdadeiro, pertinente ao ambiente de trabalho e não
                viola direitos de terceiros ou normas internas.
            </p>
            <p>
                Sugestões enviadas como <strong>"Anônimas"</strong> ocultam a identidade do
                remetente para os demais colaboradores, mas a identificação permanece visível
                para administradores do sistema, exclusivamente para fins de atribuição de
                pontuação em caso de aprovação. Veja a Política de Privacidade para mais detalhes.
            </p>
        </Section>

        <Section title="5. Condutas Vedadas">
            <p>É vedado ao usuário: (a) tentar acessar dados de outros colaboradores sem
                autorização; (b) manipular ou fraudar pontuações; (c) utilizar a plataforma
                para divulgar conteúdo ofensivo, discriminatório ou que viole normas legais;
                (d) compartilhar capturas de tela do ranking ou de dados de colegas
                fora do ambiente corporativo.</p>
        </Section>

        <Section title="6. Vigência e Alterações">
            <p>
                Estes termos são válidos enquanto durar o vínculo empregatício. A RP Métodos
                reserva-se o direito de alterar os termos, notificando os colaboradores com
                antecedência mínima de 15 dias úteis.
            </p>
        </Section>
    </div>
);

// ── Conteúdo: Política de Privacidade ─────────────────────────────────────
const PoliticaPrivacidade = () => (
    <div>
        <Section title="1. Controladora dos Dados">
            <p>
                <strong>RP Métodos Business Partners</strong> é a controladora dos dados pessoais
                tratados nesta plataforma, nos termos da Lei Geral de Proteção de Dados
                (LGPD — Lei 13.709/2018).
            </p>
            <p>
                Para questões relacionadas aos seus dados pessoais, entre em contato com o
                Encarregado de Dados (DPO) pelo e-mail corporativo da empresa ou pelo canal
                de atendimento interno.
            </p>
        </Section>

        <Section title="2. Dados Coletados">
            <p>A plataforma coleta e processa os seguintes dados pessoais:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Dados cadastrais: nome completo, e-mail corporativo, setor, função</li>
                <li>Dados de desempenho: pontuação, créditos, posição no ranking, histórico de transações</li>
                <li>Dados de reconhecimento: mensagens de "Valeu!" enviadas e recebidas</li>
                <li>Sugestões enviadas (inclusive as categorizadas como anônimas — veja item 4)</li>
                <li>Datas de férias e períodos de afastamento</li>
                <li>Registros de acesso e atividade na plataforma</li>
            </ul>
        </Section>

        <Section title="3. Bases Legais para o Tratamento">
            <p>O tratamento dos dados se baseia nas seguintes hipóteses legais (art. 7º da LGPD):</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
                <li><strong>Execução de contrato:</strong> dados necessários à gestão do vínculo empregatício</li>
                <li><strong>Legítimo interesse:</strong> métricas de desempenho e programa de incentivo interno</li>
                <li><strong>Cumprimento de obrigação legal:</strong> registros exigidos pela legislação trabalhista</li>
            </ul>
        </Section>

        <Section title="4. Sugestões Anônimas — Aviso Importante">
            <p className="font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                ⚠️ Ao enviar uma sugestão como "Anônima", seu nome será ocultado para os demais
                colaboradores. No entanto, a plataforma retém sua identificação internamente
                para possibilitar a atribuição de pontos caso a sugestão seja aprovada.
                Administradores do sistema podem identificar o autor de qualquer sugestão,
                inclusive as marcadas como anônimas.
            </p>
            <p className="mt-2">
                Isso caracteriza <strong>pseudonimização</strong>, não anonimização, nos
                termos do art. 13, §4º da LGPD. A empresa não divulgará sua identidade
                a outros colaboradores, exceto por determinação judicial ou administrativa.
            </p>
        </Section>

        <Section title="5. Compartilhamento de Dados">
            <p>
                Os dados não são compartilhados com terceiros, exceto: (a) prestadores de
                serviço de infraestrutura tecnológica (Firebase/Google LLC), sob contrato
                com cláusulas de proteção de dados; (b) por determinação legal, judicial
                ou de autoridade competente.
            </p>
        </Section>

        <Section title="6. Retenção e Exclusão">
            <p>
                Os dados são mantidos pelo período de vigência do contrato de trabalho e,
                após o desligamento, pelo prazo mínimo exigido pela legislação trabalhista
                e tributária (até 5 anos). Após esse período, os dados são anonimizados
                ou excluídos.
            </p>
        </Section>

        <Section title="7. Direitos do Titular">
            <p>Nos termos dos arts. 18 e 19 da LGPD, você tem direito a:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Confirmar a existência do tratamento e acessar seus dados</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
                <li>Solicitar a anonimização ou bloqueio de dados desnecessários</li>
                <li>Revogar o consentimento, quando aplicável</li>
                <li>Peticionar à ANPD (Autoridade Nacional de Proteção de Dados)</li>
            </ul>
            <p>Para exercer seus direitos, contate o DPO pelo canal interno.</p>
        </Section>
    </div>
);

// ── MODAL PRINCIPAL ────────────────────────────────────────────────────────
const TermsModal = ({ onAccept, isLoading, termsVersion }) => {
    const [activeTab, setActiveTab] = useState('terms');
    const [agreed, setAgreed] = useState(false);

    const tabs = [
        { id: 'terms',   label: 'Termos de Uso',          icon: FileText },
        { id: 'privacy', label: 'Política de Privacidade', icon: Shield   },
    ];

    return (
        <div className="fixed inset-0 bg-[#1B2B6B]/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

                {/* Cabeçalho */}
                <div className="px-6 py-5 bg-[#1B2B6B] rounded-t-2xl flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/10 rounded-xl">
                            <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="font-black text-white text-lg leading-tight">
                                Termos de Uso e Privacidade
                            </h2>
                            <p className="text-blue-200 text-xs mt-0.5">
                                Leia e aceite para continuar usando o RPLAY
                            </p>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mt-4">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                                        activeTab === tab.id
                                            ? 'bg-white text-[#1B2B6B]'
                                            : 'text-blue-200 hover:bg-white/10'
                                    }`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Conteúdo rolável */}
                <div className="overflow-y-auto flex-1 p-6">
                    {activeTab === 'terms'   && <TermosDeUso />}
                    {activeTab === 'privacy' && <PoliticaPrivacidade />}
                </div>

                {/* Rodapé com aceite */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0 space-y-4">

                    {/* Checkbox de aceite */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                        <div
                            onClick={() => setAgreed(!agreed)}
                            className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                agreed
                                    ? 'bg-[#1B2B6B] border-[#1B2B6B]'
                                    : 'border-gray-300 group-hover:border-[#1B2B6B]'
                            }`}
                        >
                            {agreed && <CheckSquare className="w-3.5 h-3.5 text-white fill-white" />}
                        </div>
                        <span className="text-sm text-gray-600 leading-relaxed">
                            Li e concordo com os <strong className="text-[#1B2B6B]">Termos de Uso</strong> e
                            a <strong className="text-[#1B2B6B]">Política de Privacidade</strong> da RP Métodos,
                            incluindo o tratamento dos meus dados pessoais conforme descrito.
                        </span>
                    </label>

                    {/* Botão */}
                    <button
                        onClick={onAccept}
                        disabled={!agreed || isLoading}
                        className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                            agreed && !isLoading
                                ? 'bg-[#1B2B6B] hover:bg-[#142055] text-white'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {isLoading ? 'Registrando aceite...' : (
                            <>
                                Aceitar e Continuar <ChevronRight className="w-4 h-4" />
                            </>
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                        Data e hora do aceite serão registradas conforme exigido pela LGPD (Lei 13.709/2018).
                    </p>
                    {termsVersion && (
                        <p className="text-center text-xs text-gray-300">
                            Versão {termsVersion}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TermsModal;
